import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import { ApiError, api, formatIban, formValues, type BankAccount, type BankAccountInput } from '../api.js';
import { navigate } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/audit-dialog.js';
import type { AuditDialog } from '../components/audit-dialog.js';

@customElement('bank-accounts-page')
export class BankAccountsPage extends LitElement {
  static styles = pageStyles;

  @state() private items: BankAccount[] = [];
  @state() private docCounts: Record<string, number> = {};
  @state() private loading = true;
  @state() private error = '';

  @state() private dialogOpen = false;
  @state() private editing: BankAccount | null = null;
  @state() private saving = false;
  @state() private formError = '';
  @state() private fieldErrors: Record<string, string> = {};

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = '';
    try {
      [this.items, this.docCounts] = await Promise.all([api.get<BankAccount[]>('/api/bank-accounts'), api.documentCounts('bank_account')]);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private openNew() {
    this.editing = null;
    this.resetFormState();
    this.dialogOpen = true;
  }

  private openEdit(item: BankAccount) {
    this.editing = item;
    this.resetFormState();
    this.dialogOpen = true;
  }

  private history(id: number, label: string) {
    void (this.renderRoot.querySelector('audit-dialog') as AuditDialog).show('bank_account', id, label);
  }

  private resetFormState() {
    this.formError = '';
    this.fieldErrors = {};
    this.saving = false;
  }

  private async save(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    // A checked wa-switch submits its value; unchecked submits nothing.
    const checked = (name: string) => v[name] !== undefined && v[name] !== null;
    const input: BankAccountInput = {
      label: v.label ?? '',
      currency: (v.currency ?? 'EUR') as 'EUR' | 'MKD',
      is_default: checked('is_default'),
      is_active: checked('is_active'),
      company_name: v.company_name ?? '',
      company_address: v.company_address ?? '',
      vat_number: v.vat_number ?? '',
      contact_email: v.contact_email ?? '',
      bank_name: v.bank_name ?? '',
      bank_address: v.bank_address ?? '',
      iban: v.iban ?? '',
      swift: v.swift ?? '',
      correspondent_bank_name: v.correspondent_bank_name,
      correspondent_swift: v.correspondent_swift,
      correspondent_currency: v.correspondent_bank_name ? v.correspondent_currency : null,
      correspondent_account: v.correspondent_account,
      notes: v.notes,
    };
    this.saving = true;
    this.formError = '';
    this.fieldErrors = {};
    try {
      if (this.editing) {
        await api.put(`/api/bank-accounts/${this.editing.id}`, input);
      } else {
        await api.post('/api/bank-accounts', input);
      }
      this.dialogOpen = false;
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        this.formError = err.message;
        this.fieldErrors = err.fields;
      } else {
        this.formError = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  private async deleteItem(item: BankAccount) {
    if (!confirm(`Delete bank account "${item.label}"?`)) return;
    try {
      await api.delete(`/api/bank-accounts/${item.id}`);
      await this.load();
    } catch (err) {
      this.error = (err as Error).message;
    }
  }

  render() {
    return html`
      <div class="page-header">
        <h2>Bank accounts</h2>
        <span class="spacer"></span>
        <wa-button variant="brand" @click=${this.openNew}>New bank account</wa-button>
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.renderTable()} ${this.renderDialog()}
      <audit-dialog></audit-dialog>
    `;
  }

  private renderTable() {
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    if (this.items.length === 0) return html`<div class="empty">No bank accounts yet. Each account holds the company and bank details printed on invoices.</div>`;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Account</th>
            <th>Currency</th>
            <th>Bank</th>
            <th>IBAN</th>
            <th>Company</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${this.items.map(
            (a) => html`
              <tr>
                <td data-label="Account">
                  ${a.label}
                  ${a.correspondent_bank_name ? html`<span class="sub">via ${a.correspondent_bank_name}</span>` : nothing}
                </td>
                <td data-label="Currency">
                  ${a.currency}
                  ${a.is_default ? html` <wa-tag size="small" variant="brand">default</wa-tag>` : nothing}
                </td>
                <td class="m-hide" data-label="Bank">
                  ${a.bank_name}
                  <span class="sub mono">${a.swift}</span>
                </td>
                <td class="mono wrap" data-label="IBAN">${formatIban(a.iban)}</td>
                <td class="m-hide" data-label="Company">
                  ${a.company_name}
                  <span class="sub">${a.vat_number} · ${a.contact_email}</span>
                </td>
                <td data-label="Status">
                  ${a.is_active
                    ? html`<wa-tag size="small" variant="success">active</wa-tag>`
                    : html`<wa-tag size="small" variant="neutral">inactive</wa-tag>`}
                </td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => navigate(`/documents?bank_account_id=${a.id}`)}>Documents (${this.docCounts[a.id] ?? 0})</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.history(a.id, a.label)}>History</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.openEdit(a)}>Edit</wa-button>
                  <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.deleteItem(a)}>Delete</wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }

  // wa-select and other children emit the same bubbling wa-after-hide when
  // their dropdown closes; only the dialog's own event should close it.
  private onDialogHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.dialogOpen = false;
  };

  private renderDialog() {
    const a = this.editing;
    const err = (f: string) => this.fieldErrors[f] ?? '';
    return html`
      <wa-dialog
        label=${a ? 'Edit bank account' : 'New bank account'}
        ?open=${this.dialogOpen}
        @wa-after-hide=${this.onDialogHidden}
        style="--width: min(48rem, calc(100vw - 1.5rem));"
      >
        <form id="bank-account-form" @submit=${this.save}>
          <wa-input name="label" label="Label" required placeholder="ProCredit EUR" .value=${a?.label ?? ''} hint=${err('label') || 'Short name used in pickers'}></wa-input>
          <wa-select name="currency" label="Currency" .value=${a?.currency ?? 'EUR'} hint=${err('currency')}>
            <wa-option value="EUR">EUR</wa-option>
            <wa-option value="MKD">MKD</wa-option>
          </wa-select>
          <wa-switch name="is_default" ?checked=${a?.is_default ?? false} hint=${err('is_default') || 'Invoices in this currency use the default account'}>Default for its currency</wa-switch>
          <wa-switch name="is_active" ?checked=${a?.is_active ?? true} hint="Inactive accounts are kept for history but not offered">Active</wa-switch>

          <h4>Company (printed on invoices)</h4>
          <wa-input name="company_name" label="Legal name" required placeholder="Blu Shift DOO Skopje" .value=${a?.company_name ?? ''} hint=${err('company_name')}></wa-input>
          <wa-input name="vat_number" label="VAT number" required placeholder="MK4037012500993" .value=${a?.vat_number ?? ''} hint=${err('vat_number')}></wa-input>
          <wa-input name="contact_email" label="Contact email" type="email" required .value=${a?.contact_email ?? ''} hint=${err('contact_email')}></wa-input>
          <wa-textarea name="company_address" label="Company address" required rows="2" resize="auto" .value=${a?.company_address ?? ''} hint=${err('company_address') || 'Printed as typed'}></wa-textarea>

          <h4>Bank</h4>
          <wa-input name="bank_name" label="Bank name" required placeholder="ProCredit Bank, North Macedonia" .value=${a?.bank_name ?? ''} hint=${err('bank_name')}></wa-input>
          <wa-textarea name="bank_address" label="Bank address" required rows="2" resize="auto" .value=${a?.bank_address ?? ''} hint=${err('bank_address')}></wa-textarea>
          <wa-input name="iban" label="IBAN" required placeholder="MK07 3801 7809 2403 242" .value=${a ? formatIban(a.iban) : ''} hint=${err('iban') || 'Spaces are ignored'}></wa-input>
          <wa-input name="swift" label="SWIFT / BIC" required placeholder="PRBUMK22XXX" .value=${a?.swift ?? ''} hint=${err('swift')}></wa-input>

          <h4>Correspondent bank (optional)</h4>
          <wa-input name="correspondent_bank_name" label="Correspondent bank" placeholder="ProCredit Bank AG, Frankfurt am Main DE" .value=${a?.correspondent_bank_name ?? ''} hint=${err('correspondent_bank_name')}></wa-input>
          <wa-input name="correspondent_swift" label="Correspondent SWIFT / BIC" placeholder="PRCBDEFFXXX" .value=${a?.correspondent_swift ?? ''} hint=${err('correspondent_swift')}></wa-input>
          <wa-select name="correspondent_currency" label="Correspondent currency" .value=${a?.correspondent_currency ?? 'EUR'} hint=${err('correspondent_currency')}>
            <wa-option value="EUR">EUR</wa-option>
            <wa-option value="USD">USD</wa-option>
            <wa-option value="MKD">MKD</wa-option>
          </wa-select>
          <wa-input name="correspondent_account" label="Correspondent account" placeholder="001700049" .value=${a?.correspondent_account ?? ''} hint=${err('correspondent_account')}></wa-input>

          <wa-textarea class="full" name="notes" label="Notes" rows="2" .value=${a?.notes ?? ''}></wa-textarea>
          ${this.formError ? html`<wa-callout class="form-error" variant="danger">${this.formError}</wa-callout>` : nothing}
        </form>
        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.dialogOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" form="bank-account-form" ?loading=${this.saving}>Save</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bank-accounts-page': BankAccountsPage;
  }
}
